// Painel "Tecnologia" (id interno 'sistema'). Casca leve que re-hospeda componentes
// existentes; só monta a aba ativa. Abas de topo, cada uma usando 100% da tela:
//   • Infra    (id 'infra')  = tiles do host + containers por stack + toggles admin
//   • Deploy   (id 'deploy') = deriva Gitea × produção × dev das 3 stacks
//   • Backup   (id 'backup') = snapshots restic + espelho no Drive + downloads
//   • Storage  (id 'storage')= documentos do CRM (tela inteira) + uso de disco
//   • Áudio    (id 'audio')  = Voz | Transcrição lado a lado
//   • Obsidian (iframe, SSO via cookie do CRM)
// A Clara saiu daqui em 2026-07-21: voz é o botão global da sidebar
// (ClaraVoiceContext) e o chat tem painel próprio — manter cópia aqui só duplicava.
import { useState, useEffect } from 'react'
import type { ComponentType } from 'react'
import { FiActivity, FiHeadphones, FiGitBranch, FiArchive, FiHardDrive, FiShare2, FiShield } from 'react-icons/fi'
import { SiObsidian } from 'react-icons/si'
import { useAuth } from '../contexts/AuthContext'
import { useSystemMetrics, fmtBytes } from '../hooks/useSystemMetrics'
import { SystemMonitor } from './SystemMonitor'
// ArchitectureDiagram, BibliotecaPanel, MossChat e ClaraLab ficam dormentes (não importados).
import { OmniVoiceLab } from './OmniVoiceLab'
import { WhisperLab } from './WhisperLab'
import { AutoAssignToggle } from './AutoAssignToggle'
import { LeaderFollowupToggle } from './LeaderFollowupToggle'
import { DocStorage } from './DocStorage'
import { DeployPanel } from './DeployPanel'
import { BackupPanel } from './BackupPanel'
import { IndicacoesPanel } from './IndicacoesPanel'
import { SegurancaPanel } from './SegurancaPanel'
import { LEVEL_TEXT, LEVEL_BAR, usageLevel } from '../lib/chartTheme'

// Os ids antigos ficam no union como dormentes (compat de comparações em App.tsx
// + remap de deep-links e do last-tab salvo no localStorage).
export type SistemaTab = 'infra' | 'deploy' | 'backup' | 'storage' | 'audio' | 'obsidian' | 'indicacoes' | 'seguranca'
  | 'monitor' | 'clara' | 'voz' | 'transcricao' | 'moss' | 'admin' | 'biblioteca' | 'arquitetura'

// O painel Tecnologia é liberado pela tool 'sistema', que NÃO implica admin.
// Deploy e Backup expõem versões de produção e o repositório de backup — as
// rotas são adminMiddleware, e a aba não deve nem aparecer pra quem não é admin.
type TabDef = { id: SistemaTab; label: string; icon: ComponentType<{ className?: string }>; adminOnly?: boolean }
const ALL_TABS: TabDef[] = [
  { id: 'infra', label: 'Infra', icon: FiActivity },
  { id: 'deploy', label: 'Deploy', icon: FiGitBranch, adminOnly: true },
  { id: 'backup', label: 'Backup', icon: FiArchive, adminOnly: true },
  // A trilha diz quem escreveu o quê e de onde, e a lista de sessões DERRUBA
  // gente. É exatamente o material que interessa a quem invadiu — por isso
  // adminOnly, pelo mesmo critério de Deploy/Backup.
  { id: 'seguranca', label: 'Segurança', icon: FiShield, adminOnly: true },
  { id: 'storage', label: 'Storage', icon: FiHardDrive },
  { id: 'audio', label: 'Áudio', icon: FiHeadphones },
  // Indicações expõe CPF e telefone de TERCEIROS que ainda não são clientes.
  // A tool 'sistema' não implica admin (ver comentário acima), então vale aqui
  // o mesmo critério de Deploy/Backup: relaxar depois é uma linha, vazar CPF
  // não tem desfazer.
  { id: 'indicacoes', label: 'Indicações', icon: FiShare2, adminOnly: true },
  { id: 'obsidian', label: 'Obsidian', icon: SiObsidian },
]
const isTab = (v: unknown): v is SistemaTab => ALL_TABS.some(t => t.id === v)
// Remap das abas antigas. Sem isto, um last-tab 'monitor'/'clara' salvo no
// localStorage cairia numa aba inexistente e o painel abriria vazio.
const remapTab = (v: unknown): SistemaTab | null => {
  if (v === 'voz' || v === 'transcricao') return 'audio'
  if (v === 'monitor' || v === 'moss' || v === 'admin' || v === 'clara') return 'infra'
  return isTab(v) ? (v as SistemaTab) : null
}

interface SistemaPanelProps {
  initialTab?: SistemaTab | null
  initialPath?: string | null
  onTabChange?: (tab: SistemaTab) => void
}

// Cápsula segmentada estilo iOS (glass). Mobile: scroll horizontal, ícone-only, nada some.
function TabBar({ value, onChange, tabs }: { value: SistemaTab; onChange: (t: SistemaTab) => void; tabs: TabDef[] }) {
  return (
    <div className="flex px-3 pt-3 shrink-0 overflow-x-auto">
      <div className="glass-panel neu-seg rounded-full p-1 flex gap-1 flex-nowrap mx-auto">
        {tabs.map(t => {
          const active = value === t.id
          const Icon = t.icon
          return (
            <button key={t.id} onClick={() => onChange(t.id)} title={t.label}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-medium whitespace-nowrap transition-colors ${
                active
                  ? 'neu-relief bg-primary text-primary-foreground'
                  : 'neu-seg-btn text-muted-foreground hover:text-foreground'
              }`}>
              <Icon className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function SistemaPanel({ initialTab = null, initialPath = null, onTabChange }: SistemaPanelProps) {
  const { authenticatedFetch, user } = useAuth()

  const isAdmin = !!user?.isAdmin
  const tabs = ALL_TABS.filter(t => isAdmin || !t.adminOnly)

  const [tab, setTab] = useState<SistemaTab>(() => {
    let stored: string | null = null
    try { stored = localStorage.getItem('sistema-last-tab') } catch { /* */ }
    return remapTab(initialTab) ?? remapTab(stored) ?? 'infra'
  })
  // Aba admin-only alcançada por deep-link/last-tab por quem não é admin cairia
  // numa aba sem botão correspondente — sem isto, o painel abre "travado".
  const safeTab: SistemaTab = tabs.some(t => t.id === tab) ? tab : 'infra'

  // Métricas — Infra (tiles/containers) e Storage (uso de disco) as consomem.
  const m = useSystemMetrics({ enabled: safeTab === 'infra' || safeTab === 'storage' })

  const changeTab = (t: SistemaTab) => {
    setTab(t)
    try { localStorage.setItem('sistema-last-tab', t) } catch { /* */ }
    onTabChange?.(t)
  }

  // initialPath (deep-link de nota) → abre o Obsidian (vault).
  useEffect(() => {
    if (initialPath) changeTab('obsidian')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPath])

  return (
    <div className="h-full flex flex-col">
      <TabBar value={safeTab} onChange={changeTab} tabs={tabs} />
      <div className="flex-1 min-h-0">

        {/* INFRA — saúde do host e da stack Docker + os toggles de operação. */}
        {safeTab === 'infra' && (
          <div className="h-full min-h-0 flex flex-col p-4 sm:p-5 overflow-y-auto">
            <div className="w-full max-w-[1600px] mx-auto flex flex-col gap-4 flex-1 min-h-0">
              <div className="flex-1 min-h-0"><SystemMonitor m={m} embedded /></div>
              {user?.isAdmin && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 shrink-0">
                  <AutoAssignToggle />
                  <LeaderFollowupToggle />
                </div>
              )}
            </div>
          </div>
        )}

        {/* DEPLOY — o que já subiu pra produção (Gitea × prod × dev). */}
        {safeTab === 'deploy' && <div className="h-full min-h-0 p-4 sm:p-5"><DeployPanel /></div>}

        {/* BACKUP — snapshots restic, espelho no Drive e downloads. */}
        {safeTab === 'backup' && <div className="h-full min-h-0 p-4 sm:p-5"><BackupPanel /></div>}

        {/* INDICAÇÕES — fila da PWA pública /indica + gestão dos links.
            SEM neu-forms aqui: os campos do painel já trazem .neu-inset no
            próprio invólucro, e o neu-forms aplicaria um SEGUNDO poço no
            <input> descendente — o resultado é a moldura dupla. */}
        {safeTab === 'indicacoes' && <div className="h-full min-h-0 p-4 sm:p-5"><IndicacoesPanel /></div>}

        {/* STORAGE — documentos do CRM com a tela inteira (antes disputava altura
            com o monitor numa coluna) + os mounts do host no topo. */}
        {safeTab === 'storage' && (
          <div className="h-full min-h-0 flex flex-col gap-4 p-4 sm:p-5">
            {m.metrics?.host?.disk?.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 shrink-0">
                {m.metrics.host.disk.map(d => (
                  <div key={d.mount} className="glass-panel neu-card rounded-2xl px-4 py-3 flex flex-col gap-1.5"
                    title={`${d.mount}: ${fmtBytes(d.used_b)} usados de ${fmtBytes(d.total_b)}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[11px] font-semibold text-foreground truncate">{d.mount}</span>
                      <span className={`text-[12px] font-semibold tabular-nums ${LEVEL_TEXT[usageLevel(d.usage_pct)]}`}>{d.usage_pct}%</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-muted/70 overflow-hidden neu-gauge">
                      <div className={`h-full rounded-full ${LEVEL_BAR[usageLevel(d.usage_pct)]}`}
                        style={{ width: `${Math.max(0, Math.min(100, d.usage_pct))}%` }} />
                    </div>
                    <span className="text-[10px] text-muted-foreground tabular-nums">{fmtBytes(d.used_b)} / {fmtBytes(d.total_b)}</span>
                  </div>
                ))}
              </div>
            ) : null}
            <div className="flex-1 min-h-0"><DocStorage authenticatedFetch={authenticatedFetch} isAdmin={!!user?.isAdmin} /></div>
          </div>
        )}

        {/* ÁUDIO — Voz | Transcrição lado a lado. 100% da tela. */}
        {/* neu-forms no wrapper cobre os campos dos dois labs de uma vez */}
        {safeTab === 'audio' && (
          <div className="h-full min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 sm:p-5 overflow-y-auto lg:overflow-hidden">
            <div className="min-w-0 lg:h-full lg:overflow-y-auto glass-panel neu-bar neu-forms rounded-2xl p-4 sm:p-5"><OmniVoiceLab authenticatedFetch={authenticatedFetch} /></div>
            <div className="min-w-0 lg:h-full lg:overflow-y-auto glass-panel neu-bar neu-forms rounded-2xl p-4 sm:p-5"><WhisperLab authenticatedFetch={authenticatedFetch} /></div>
          </div>
        )}

        {safeTab === 'seguranca' && <SegurancaPanel authenticatedFetch={authenticatedFetch} />}

        {safeTab === 'obsidian' && <ObsidianTab />}
      </div>
    </div>
  )
}

// Obsidian completo (KasmVNC) servido pela própria origem do CRM em /obsidian/
// (proxy no nginx do frontend → container `obsidian`), com SSO via cookie de sessão.
function ObsidianTab() {
  return (
    <iframe
      src="/obsidian/"
      title="Obsidian"
      className="w-full h-full border-0"
      allow="clipboard-read; clipboard-write; fullscreen"
    />
  )
}
